import { useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import type { HistoryItem } from '../types';
import { getMyRuns, loadSupabaseSession } from '../services/api';

const HIDDEN_HISTORY_KEY = 'academic-review-history-hidden';

type HistoryViewMode = 'card' | 'list';
type HistoryItemView = HistoryItem & {
  absoluteTime: string;
  relativeTime: string;
};

function loadHiddenHistoryIds(): Set<string> {
  try {
    const raw = JSON.parse(window.localStorage.getItem(HIDDEN_HISTORY_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw.map((item) => String(item)) : []);
  } catch {
    return new Set();
  }
}

function saveHiddenHistoryIds(ids: Set<string>) {
  try {
    window.localStorage.setItem(HIDDEN_HISTORY_KEY, JSON.stringify([...ids].slice(0, 1000)));
  } catch {
    // noop
  }
}

function formatProgress(progress: number): string {
  return `${Math.max(0, Math.min(100, Math.round(progress)))}%`;
}

function getStatusAccent(status: string): string {
  if (status === 'completed') return 'emerald';
  if (status === 'running') return 'cyan';
  if (status === 'failed') return 'rose';
  return 'slate';
}

function getStatusHint(item: HistoryItem, labels: { completed: string; progress: string; failed: string }): string {
  if (item.status === 'completed') return labels.completed;
  if (item.status === 'running' || item.status === 'queued') return labels.progress;
  return labels.failed;
}

function formatAbsoluteTime(timestamp: number, locale: string): string {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '-';
  return new Intl.DateTimeFormat(locale === 'zh' ? 'zh-CN' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp);
}

function formatRelativeTime(timestamp: number, locale: string): string {
  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale === 'zh' ? 'zh-CN' : 'en-US', { numeric: 'auto' });

  if (Math.abs(diffMinutes) < 60) return rtf.format(-diffMinutes, 'minute');
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 48) return rtf.format(-diffHours, 'hour');
  const diffDays = Math.round(diffHours / 24);
  return rtf.format(-diffDays, 'day');
}

function ViewToggle({ viewMode, setViewMode }: { viewMode: HistoryViewMode; setViewMode: (mode: HistoryViewMode) => void }) {
  return (
    <div className="history-view-toggle" aria-label="history view toggle">
      <button
        type="button"
        className={`history-view-btn ${viewMode === 'card' ? 'active' : ''}`}
        onClick={() => setViewMode('card')}
        aria-pressed={viewMode === 'card'}
      >
        卡片
      </button>
      <button
        type="button"
        className={`history-view-btn ${viewMode === 'list' ? 'active' : ''}`}
        onClick={() => setViewMode('list')}
        aria-pressed={viewMode === 'list'}
      >
        列表
      </button>
    </div>
  );
}

export default function HistoryPanel() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const history = useAppStore((state) => state.history);
  const removeFromHistory = useAppStore((state) => state.removeFromHistory);
  const clearHistory = useAppStore((state) => state.clearHistory);
  const [hiddenRunIds, setHiddenRunIds] = useState<Set<string>>(() => loadHiddenHistoryIds());
  const [viewMode, setViewMode] = useState<HistoryViewMode>(() => {
    const stored = window.localStorage.getItem('academic-history-view-mode');
    return stored === 'list' ? 'list' : 'card';
  });
  const [remoteRuns, setRemoteRuns] = useState<Array<Record<string, any>>>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState('');
  const [useRemote, setUseRemote] = useState(Boolean(loadSupabaseSession() || localStorage.getItem('academic-review-auth-token')));

  const statusLabels = {
    completed: t('review.viewReview'),
    progress: t('run.progress'),
    failed: t('error.title'),
  };

  const historyItems = useMemo<HistoryItemView[]>(() => {
    const source = useRemote ? remoteRuns.map((item) => ({
      runId: String(item.id || item.run_id || ''),
      topic: String(item.topic || ''),
      status: String(item.status || 'queued'),
      createdAt: new Date(String(item.created_at || item.createdAt || Date.now())).getTime(),
      progress: Number(item.progress || (item.status === 'completed' ? 100 : item.status === 'running' ? 50 : 0)),
    })) : history;

    return source
      .filter((item) => item.runId && !hiddenRunIds.has(item.runId))
      .map((item) => ({
        ...item,
        absoluteTime: formatAbsoluteTime(item.createdAt, i18n.language),
        relativeTime: formatRelativeTime(item.createdAt, i18n.language),
      }));
  }, [history, hiddenRunIds, i18n.language, remoteRuns, useRemote]);

  useEffect(() => {
    window.localStorage.setItem('academic-history-view-mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const onAuthChange = () => {
      const remote = Boolean(loadSupabaseSession() || localStorage.getItem('academic-review-auth-token'));
      setUseRemote(remote);
    };
    window.addEventListener('academic-review-auth-change', onAuthChange);
    return () => window.removeEventListener('academic-review-auth-change', onAuthChange);
  }, []);

  useEffect(() => {
    if (!useRemote) {
      setRemoteRuns([]);
      setRemoteError('');
      return;
    }
    let cancelled = false;
    setRemoteLoading(true);
    setRemoteError('');
    getMyRuns(50, 0)
      .then((data) => {
        if (cancelled) return;
        setRemoteRuns(Array.isArray((data as any).runs) ? (data as any).runs : []);
      })
      .catch((error) => {
        if (cancelled) return;
        setRemoteError(error instanceof Error ? error.message : String(error));
        setUseRemote(false);
      })
      .finally(() => {
        if (!cancelled) setRemoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [useRemote]);

  const handleSelect = (item: HistoryItem) => {
    navigate(`/runs/${item.runId}`);
  };

  const hideRunLocally = (runId: string) => {
    setHiddenRunIds((previous) => {
      const next = new Set(previous);
      next.add(runId);
      saveHiddenHistoryIds(next);
      return next;
    });
    removeFromHistory(runId);
    if (useRemote) {
      setRemoteRuns((current) => current.filter((item) => String(item.id || item.run_id || '') !== runId));
    }
  };

  const handleRemove = (event: MouseEvent, runId: string) => {
    event.stopPropagation();
    hideRunLocally(runId);
  };

  const handleClearLocal = () => {
    const next = new Set(hiddenRunIds);
    for (const item of history) next.add(item.runId);
    for (const item of remoteRuns) next.add(String(item.id || item.run_id || ''));
    saveHiddenHistoryIds(next);
    setHiddenRunIds(next);
    clearHistory();
    setRemoteRuns([]);
  };

  const handleCardKeyDown = (event: KeyboardEvent, item: HistoryItem) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect(item);
    }
  };

  if (viewMode === 'list') {
    return (
      <section className="history-panel history-list-page" style={{ position: 'relative', zIndex: 2 }}>
        <header className="history-list-toolbar">
          <div className="history-list-title-block">
            <p className="history-kicker">Research Atlas</p>
            <h2 className="history-title">{t('nav.history')}</h2>
            <span className="history-list-meta">{historyItems.length} 条{useRemote ? '远端' : '本地'}可见记录</span>
          </div>
          <div className="history-list-actions">
            <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
            <button
              type="button"
              className="history-clear-btn"
              onClick={handleClearLocal}
              disabled={history.length === 0 && remoteRuns.length === 0}
            >
              清空历史
            </button>
          </div>
        </header>

        {(remoteError || remoteLoading) && (
          <div className={remoteError ? 'account-alert account-alert-error' : 'account-alert'}>
            {remoteError || '正在加载远端历史...'}
          </div>
        )}

        <div className="history-table-frame">
          <table className="history-table">
            <thead>
              <tr>
                <th>状态</th>
                <th>主题</th>
                <th>进度</th>
                <th>创建时间</th>
                <th>Run ID</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {historyItems.length === 0 ? (
                <tr>
                  <td className="history-list-empty" colSpan={6}>暂无历史记录</td>
                </tr>
              ) : historyItems.map((item) => (
                <tr key={item.runId} onClick={() => handleSelect(item)} title={item.absoluteTime}>
                  <td><span className={`status-tag status-${item.status}`}>{t(`status.${item.status}`, item.status)}</span></td>
                  <td className="history-table-topic">{item.topic || '(无主题)'}</td>
                  <td>
                    <div className="history-table-progress">
                      <span>{formatProgress(item.progress)}</span>
                      <i><b style={{ width: formatProgress(item.progress) }} /></i>
                    </div>
                  </td>
                  <td>
                    <span className="history-table-time">{item.relativeTime}</span>
                    <small>{item.absoluteTime}</small>
                  </td>
                  <td><span className="history-id">#{item.runId.slice(-8)}</span></td>
                  <td>
                    <button
                      type="button"
                      className="history-row-action"
                      onClick={(event) => handleRemove(event, item.runId)}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  return (
    <section className="history-panel history-atlas" style={{ position: 'relative', zIndex: 2 }}>
      <header className="history-hero">
        <div>
          <p className="history-kicker">Research Atlas</p>
          <h2 className="history-title">{t('nav.history')}</h2>
          <p className="history-subtitle">已登录时这里读取服务端个人历史；未登录时保留本地历史兜底。</p>
        </div>
        <div className="history-hero-actions">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <div className="history-summary-card">
            <span className="history-summary-label">Runs</span>
            <strong className="history-summary-value">{historyItems.length}</strong>
          </div>
          <button
            type="button"
            className="history-clear-btn"
            onClick={handleClearLocal}
            disabled={history.length === 0 && remoteRuns.length === 0}
          >
            清空历史
          </button>
        </div>
      </header>

      {(remoteError || remoteLoading) && (
        <div className={remoteError ? 'account-alert account-alert-error' : 'account-alert'}>
          {remoteError || '正在加载远端历史...'}
        </div>
      )}

      {historyItems.length === 0 && (
        <div className="history-empty history-empty-card">
          <div className="history-empty-copy">
            <strong>{t('nav.history')}</strong>
            <span>暂无历史记录，启动一次检索后这里会形成你的任务索引。</span>
          </div>
        </div>
      )}

      {historyItems.length > 0 && (
        <div className="history-grid history-grid-card">
          {historyItems.map((item, index) => {
            const accent = getStatusAccent(item.status);
            return (
              <article
                key={item.runId}
                role="button"
                tabIndex={0}
                className={`history-item history-card-${accent}`}
                onClick={() => handleSelect(item)}
                onKeyDown={(event) => handleCardKeyDown(event, item)}
                style={{ animationDelay: `${index * 50}ms` }}
                title={item.absoluteTime}
              >
                <div className="history-card-topline">
                  <span className={`status-tag status-${item.status}`}>{t(`status.${item.status}`, item.status)}</span>
                  <span className="history-id">#{item.runId.slice(-8)}</span>
                </div>

                <div className="history-card-main">
                  <h3 className="history-topic">{item.topic || '(无主题)'}</h3>
                  <p className="history-card-hint">{getStatusHint(item, statusLabels)}</p>
                </div>

                <div className="history-card-footer">
                  <div className="history-progress-cluster">
                    <div className="history-time-row">
                      <span className="history-progress-label">{t('run.progress')}</span>
                      <span className="history-time-chip" title={item.absoluteTime}>{item.relativeTime}</span>
                    </div>
                    <div className="history-progress-track">
                      <div className="history-progress-fill" style={{ width: formatProgress(item.progress) }} />
                    </div>
                  </div>
                  <div className="history-progress-value">{formatProgress(item.progress)}</div>
                </div>
                <button
                  type="button"
                  className="history-remove-chip"
                  onClick={(event) => handleRemove(event, item.runId)}
                >
                  删除
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
